// TS twin of iOS `WheelLoader.swift`.
//
// Orchestrates the load pipeline: assembler poll → model download → renderer
// hand-off. Owns the retry budget (max 5, matching iOS commit b08a345) and
// the skip-poll-if-cached optimisation so a download-only failure can retry
// without re-running the assembler.
//
// State exposed via getState() + subscribe() (the TS equivalent of the iOS
// @Published properties). Telemetry event names + property keys mirror iOS
// exactly so PostHog dashboards can union both platforms.
//
// Renderer hand-off is a callback (`setWheelModel`) instead of a typed
// dependency — keeps the loader decoupled from the WheelRenderer protocol
// (task C). main.ts wires the real renderer's setWheelModel into this
// callback at startup.

import { atoLog } from '@/services/ato-log'
import type { AppEnvironment } from '@/models/app-environment'
import type { AssemblyCode } from '@/models/assembly-code'
import type { AppClipInvocation } from '@/models/app-clip-invocation'
import type { WheelDetails } from '@/models/wheel-details'
import { wheelSize, type WheelSize } from '@/models/wheel-size'
import { glbDiameterInches } from '@/models/base-diameter'
import type { PollResult } from '@/services/assembler-service'
import type { DownloadResult } from '@/services/model-downloader'

// ── Public types ────────────────────────────────────────────────────────

export interface WheelInfo {
  brandName: string
  modelName: string
  brandLogoURL: string | null
  brandLogoDarkURL: string | null
  sizes: WheelSize[]
  selectedSize: WheelSize | null
  assembledSize: WheelSize | null
  /** ADR-104 — `sizes` are the sizes SOLD, not the mesh's own (WheelDetails). */
  sizesAreOffered: boolean
}

export const EMPTY_WHEEL_INFO: WheelInfo = {
  brandName: '',
  modelName: '',
  brandLogoURL: null,
  brandLogoDarkURL: null,
  sizes: [],
  selectedSize: null,
  assembledSize: null,
  sizesAreOffered: false,
}

// Mirrors iOS WheelInfo.bundledDemo — the canonical demo wheel surfaced for
// the bundled-USDZ fallback (App Store reviewer flow) and SwiftUI previews.
export const BUNDLED_DEMO_WHEEL_INFO: WheelInfo = (() => {
  const sizes = ['24x12', '22x12', '22x10', '20x10', '20x9']
    .map(wheelSize)
    .filter((s): s is WheelSize => s != null)
  return {
    brandName: 'AutoViz',
    modelName: 'AV Mesh Demo',
    brandLogoURL: null,
  brandLogoDarkURL: null,
    sizes,
    selectedSize: sizes[0] ?? null,
    assembledSize: null,
    sizesAreOffered: false,
  }
})()

export interface WheelLoaderState {
  readonly isLoading: boolean
  readonly isPollingAssembler: boolean
  readonly pollingError: string | null
  readonly downloadError: string | null
  readonly hasLoadedModel: boolean
  readonly wheelInfo: WheelInfo
  /** The loaded GLB's own size, whole inches — the wheel's default size
   *  when the payload has none (2026-10-05). Null until a model measures,
   *  or when the GLB is not plausibly a wheel. */
  readonly glbDiameterInches: number | null
  /** Fraction of the model received, 0–1 (?use_ios_ui download card).
   *  Stays 0 when the server declares no length. */
  readonly downloadProgress: number
}

export interface AssemblerLike {
  poll(opts: {
    code: AssemblyCode
    env: AppEnvironment
    signal?: AbortSignal
  }): Promise<PollResult>
}

export interface DownloaderLike {
  download(opts: {
    url: URL
    signal?: AbortSignal
    onProgress?: (fraction: number) => void
  }): Promise<DownloadResult>
}

export type WheelModelHandoff = (input: {
  bytes: ArrayBuffer
  contentType: string | null
}) => Promise<{ faceDiameterM: number | null } | void>

export interface WheelLoaderConfig {
  assembler: AssemblerLike
  downloader: DownloaderLike
  setWheelModel: WheelModelHandoff
  maxRetries?: number
  /** Fires when a poll lands assembler details — the boundary where
   *  main.ts inspects the payload (e.g. posts the wheel_size_missing
   *  messenger notice). The loader itself stays transport-free
   *  (2026-09-07). */
  onWheelDetails?: (details: WheelDetails) => void
}

export const EXHAUSTED_MESSAGE =
  'Too many failed attempts. Please close and reopen the AR experience.'

/**
 * Nothing to load — the AR experience was opened without a code or a
 * model URL.
 *
 * This branch used to log and return, leaving isLoading untouched, so
 * the app sat on "Loading wheel…" for ever waiting on a request that
 * was never made (2026-08-24 LUAT: "there was no code passed to the
 * arex and it sits there trying to load the wheel"). A load that cannot
 * start is a FAILED load, and has to say so.
 */
export const NO_INVOCATION_MESSAGE =
  'No wheel was specified. Please reopen from the wheel you want to view.'

/**
 * Terminal error for a load, or null while it may still succeed.
 *
 * Polling and download failures are separate fields because they retry
 * differently, but every consumer wants the same question answered:
 * has this stopped, and what do I tell the user? Pure so the app does
 * not re-derive it (and get it subtly different).
 */
export function loadFailureMessage(state: {
  pollingError: string | null
  downloadError: string | null
  hasLoadedModel: boolean
}): string | null {
  // A model that arrived beats any earlier stumble on the way to it.
  if (state.hasLoadedModel) return null
  return state.pollingError ?? state.downloadError ?? null
}

type Listener = (state: WheelLoaderState) => void

// ── WheelLoader ─────────────────────────────────────────────────────────

export class WheelLoader {
  private state: WheelLoaderState = {
    isLoading: false,
    isPollingAssembler: false,
    pollingError: null,
    downloadError: null,
    hasLoadedModel: false,
    wheelInfo: EMPTY_WHEEL_INFO,
    glbDiameterInches: null,
    downloadProgress: 0,
  }
  private readonly listeners = new Set<Listener>()

  private readonly assembler: AssemblerLike
  private readonly downloader: DownloaderLike
  private readonly setWheelModel: WheelModelHandoff
  private readonly maxRetries: number
  private readonly onWheelDetails: ((details: WheelDetails) => void) | undefined

  private activeController: AbortController | null = null
  private lastModelURL: URL | null = null
  private retryCount = 0

  constructor(config: WheelLoaderConfig) {
    this.assembler = config.assembler
    this.downloader = config.downloader
    this.setWheelModel = config.setWheelModel
    this.maxRetries = config.maxRetries ?? 5
    this.onWheelDetails = config.onWheelDetails
  }

  getState(): WheelLoaderState {
    return this.state
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /// Fresh load. Resets retry budget; cancels any in-flight load.
  load(invocation: AppClipInvocation): void {
    this.retryCount = 0
    this.lastModelURL = null
    this.startLoading(invocation)
  }

  /// Budget-aware retry. Skips re-poll when the previous poll succeeded
  /// (the assembler result is short-lived but we still have the model URL).
  retry(invocation: AppClipInvocation): void {
    if (this.retryCount >= this.maxRetries) {
      if (this.state.pollingError != null) {
        this.update({ pollingError: EXHAUSTED_MESSAGE })
      } else {
        this.update({ downloadError: EXHAUSTED_MESSAGE })
      }
      return
    }
    this.retryCount += 1

    if (this.lastModelURL != null && this.state.pollingError == null) {
      this.update({ downloadError: null })
      this.activeController?.abort()
      this.activeController = new AbortController()
      void this.loadRemoteModel(this.lastModelURL, this.activeController.signal)
    } else {
      this.update({ pollingError: null, downloadError: null })
      this.startLoading(invocation)
    }
  }

  /// Cancels in-flight load + clears overlay state.
  cancel(): void {
    this.activeController?.abort()
    this.activeController = null
    this.update({
      pollingError: null,
      downloadError: null,
      isPollingAssembler: false,
    })
  }

  /// Bundled-model path — hands the buffer straight to the renderer with the
  /// canonical demo wheelInfo. No polling, no download.
  async loadBundled(input: {
    bytes: ArrayBuffer
    contentType: string | null
  }): Promise<void> {
    this.update({ isLoading: true })
    try {
      const measured = await this.setWheelModel(input)
      this.update({
        glbDiameterInches: glbDiameterInches(measured?.faceDiameterM),
        hasLoadedModel: true,
        wheelInfo: BUNDLED_DEMO_WHEEL_INFO,
        isLoading: false,
      })
    } catch (error) {
      atoLog.event('bundled_load_failed', atoLog.errorProperties(error))
      this.update({ isLoading: false })
    }
  }

  // ── internal orchestration ─────────────────────────────────────────────

  private startLoading(invocation: AppClipInvocation): void {
    this.activeController?.abort()
    this.activeController = new AbortController()
    const signal = this.activeController.signal

    if (invocation.usesPollingFlow && invocation.code != null) {
      atoLog.debug(
        `▶️ startLoading branch=polling code=${invocation.code.value}`,
      )
      void this.pollAndLoad(invocation.code, invocation.env, signal)
    } else if (invocation.modelURL != null) {
      atoLog.debug(
        `▶️ startLoading branch=directURL url=${invocation.modelURL.toString()}`,
      )
      void this.loadRemoteModel(invocation.modelURL, signal)
    } else {
      atoLog.event('load_failed', {
        reason: 'invocation_has_no_code_or_url',
      })
      // Surface it. Silence here reads to the user as "still working".
      this.update({
        isLoading: false,
        isPollingAssembler: false,
        pollingError: NO_INVOCATION_MESSAGE,
      })
    }
  }

  private async pollAndLoad(
    code: AssemblyCode,
    env: AppEnvironment,
    signal: AbortSignal,
  ): Promise<void> {
    this.update({ isPollingAssembler: true, pollingError: null })
    const pollStart = Date.now()

    let result: PollResult
    try {
      result = await this.assembler.poll({ code, env, signal })
    } catch (error) {
      if (isAbortError(error)) {
        this.update({ isPollingAssembler: false })
        return
      }
      const message = error instanceof Error ? error.message : String(error)
      this.update({
        isPollingAssembler: false,
        pollingError: message,
      })
      return
    }

    if (result.details?.partID != null) {
      atoLog.register({ part_id: result.details.partID })
    }
    if (result.details != null) {
      this.onWheelDetails?.(result.details)
    }
    atoLog.event('poll_complete', {
      env,
      duration_s: (Date.now() - pollStart) / 1000,
    })
    this.lastModelURL = result.modelURL

    const nextWheelInfo =
      result.details != null
        ? projectWheelInfo(result.details)
        : this.state.wheelInfo
    this.update({
      isPollingAssembler: false,
      wheelInfo: nextWheelInfo,
    })

    await this.loadRemoteModel(result.modelURL, signal)
  }

  private async loadRemoteModel(
    url: URL,
    signal: AbortSignal,
  ): Promise<void> {
    this.update({ isLoading: true, downloadError: null, downloadProgress: 0 })
    const loadStart = Date.now()

    try {
      const downloaded = await this.downloader.download({
        url,
        signal,
        // Whole percents only: every update re-renders every subscriber,
        // and a large GLB arrives in hundreds of chunks.
        onProgress: (fraction) => {
          const percent = Math.floor(fraction * 100) / 100
          if (percent !== this.state.downloadProgress) this.update({ downloadProgress: percent })
        },
      })
      const measured = await this.setWheelModel({
        bytes: downloaded.bytes,
        contentType: downloaded.contentType,
      })
      this.retryCount = 0
      atoLog.event('model_load_complete', {
        renderer: 'webar',
        duration_s: (Date.now() - loadStart) / 1000,
      })
      this.update({
        glbDiameterInches: glbDiameterInches(measured?.faceDiameterM),
        hasLoadedModel: true,
        isLoading: false,
        downloadProgress: 1,
      })
    } catch (error) {
      if (isAbortError(error)) {
        atoLog.debug('↩️ loadRemoteModel cancelled')
        this.update({ isLoading: false })
        return
      }
      const message = error instanceof Error ? error.message : String(error)
      // Remote-URL failures are already structured-logged by ModelDownloader
      // (model_download_failed). Don't double-emit; just surface the message
      // on the published state so the UI banner can show it.
      this.update({ downloadError: message, isLoading: false })
    }
  }

  private update(patch: Partial<WheelLoaderState>): void {
    this.state = { ...this.state, ...patch }
    for (const listener of Array.from(this.listeners)) {
      listener(this.state)
    }
  }
}

// ── helpers ─────────────────────────────────────────────────────────────

function projectWheelInfo(details: WheelDetails): WheelInfo {
  const sizes = [...details.sizes]
  // ADR-104 — open at the payload's size (a modeled selection, or an offered
  // set's default written in the same place). An offered list's first entry is
  // a size the wheel is SOLD in, not its own, so it is never the fallback —
  // the GLB estimate decides instead.
  const selectedSize =
    details.assembledSize ??
    (details.sizesAreOffered ? null : sizes[0]) ??
    null
  return {
    brandName: details.brandName,
    modelName: details.modelName,
    brandLogoURL: details.brandLogoURL?.toString() ?? null,
    brandLogoDarkURL: details.brandLogoDarkURL?.toString() ?? null,
    sizes,
    selectedSize,
    assembledSize: details.assembledSize,
    sizesAreOffered: details.sizesAreOffered,
  }
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}
